import { useState, useEffect, useCallback, useRef } from 'react';
import { apiRequest } from '../config/api';
import { useAuth } from './useAuth';

export interface InAppNotification {
  id: string;
  memberId: string;
  type: string;
  title?: string;
  body?: string;
  actorName?: string;
  actorPhotoUrl?: string | null;
  targetType?: string | null;
  targetId?: string | null;
  threadId?: string;
  threadName?: string;
  senderName?: string;
  content?: string;
  read: boolean;
  createdAt: Date;
}

interface NotificationsResponse {
  success: boolean;
  notifications: Array<{
    id: string;
    memberId: string;
    type: string;
    title?: string;
    body?: string;
    actorName?: string;
    actorPhotoUrl?: string | null;
    targetType?: string | null;
    targetId?: string | null;
    threadId?: string;
    threadName?: string;
    senderName?: string;
    content?: string;
    read: boolean;
    createdAt: string | null;
  }>;
  unreadCount: number;
}

const POLL_INTERVAL = 30000; // 30 seconds

export function useInAppNotifications() {
  const { profile } = useAuth();
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchNotifications = useCallback(async () => {
    if (!profile?.id) return;
    try {
      const data = await apiRequest<NotificationsResponse>('/api/threads/notifications/list');
      const items = (data.notifications || []).map((n) => ({
        ...n,
        createdAt: n.createdAt ? new Date(n.createdAt) : new Date(),
      }));
      setNotifications(items);
      setUnreadCount(data.unreadCount || 0);
    } catch (error) {
      // Silently fail - notifications are non-critical
      console.error('Error fetching notifications:', error);
    }
  }, [profile?.id]);

  useEffect(() => {
    fetchNotifications();

    // Poll for new notifications
    intervalRef.current = setInterval(fetchNotifications, POLL_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchNotifications]);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await apiRequest(`/api/threads/notifications/${notificationId}/read`, { method: 'PATCH' });
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await apiRequest('/api/threads/notifications/mark-all-read', { method: 'POST' });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, []);

  return { notifications, unreadCount, markAsRead, markAllAsRead };
}
